import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  forwardRef,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ScrollBarService } from '@nrwl/scrollbar-service';
import { MatRadioButton, MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { RxIconModule } from '@nrwl/rx-icon';

@Component({
  selector: 'rx-list-radio-item',
  templateUrl: './rx-list-radio-item.component.html',
  styleUrls: ['./rx-list-radio-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatRadioModule, RxIconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RxListRadioItemComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RxListRadioItemComponent),
      multi: true,
    },
  ],
})
export class RxListRadioItemComponent
  implements OnInit, OnDestroy, ControlValueAccessor, OnChanges, Validator
{
  private _destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() title: string;
  @Input() position = 'right';
  @Input() value: boolean;
  @Input() description: string;
  @Input() data: boolean;
  @Input() icon: string;
  @Input() showDragHandle: false;
  @Input() showMenuHandle: false;
  @Input() showIconCheck: boolean;
  @Input() actionMenu: TemplateRef<any>;
  @Input() actionMenuContext: any;

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter();

  // @Output()
  // public selected: EventEmitter<any> = new EventEmitter();

  public control: FormControl;

  public insideVisibleArea = true;

  private parentVisibleArea: { id: any; rect: any };
  private parentRef: any;

  public onChange = (value: boolean) => {};
  public onTouched = () => {};

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    private scrollService: ScrollBarService,
    private viewContainerRef: ViewContainerRef,
  ) {}

  public ngOnInit() {
    // TODO: WARNING: Optimization related
    // - Show simplified version of element outside visible area.
    // - Instead of passing parent visible rectangle with @Input,
    // - we look for closest RxAccordion component to enable rendering optimization
    let parent = this.viewContainerRef.element.nativeElement;
    while (parent) {
      if (parent.localName === 'rx-accordion') {
        this.parentRef = parent;
        this.setupOptimization(parent);
        break;
      }
      parent = parent.parentElement;
    }

    // Process first time run
    this.render();

    // Process scroll events
    this.scrollService.scrolled.pipe(takeUntil(this._destroy$)).subscribe((scroll) => {
      if (scroll) {
        this.render(scroll);
      }
    });
  }

  public setupOptimization(element) {
    const id = element.getAttribute('data-element-id');
    const rect = element.getBoundingClientRect();
    this.parentVisibleArea = { id: id, rect: rect };
  }

  public render(scrollData?: any) {
    // Display host if parent visible area is undefined
    if (!this.parentVisibleArea) {
      this.insideVisibleArea = true;
      return;
    }

    // HACK: getAttribute not available onInit when using optimization in Dialogs
    if (!this.parentVisibleArea.id) {
      this.parentVisibleArea.id = this.parentRef.getAttribute('data-element-id');
    }

    // Do nothing on scroll event with parent visible area id mismatch
    if (scrollData && this.parentVisibleArea.id !== scrollData.id) {
      return;
    }

    // Display SVG placeholder outside visible area
    const offset = 100;
    const top = (scrollData || this.parentVisibleArea).rect.top - offset;
    const bottom = (scrollData || this.parentVisibleArea).rect.bottom + offset;

    const selfTop = this.elementRef.nativeElement.getBoundingClientRect().top;
    if (top < selfTop && bottom > selfTop) {
      this.insideVisibleArea = true;
      if (!this.changeDetectorRef['destroyed']) {
        this.changeDetectorRef.detectChanges();
      }
    } else {
      this.insideVisibleArea = false;
      if (!this.changeDetectorRef['destroyed']) {
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  public ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges | any): void {
    // trigger validation
    this.onChange(this.value);
  }

  public onModelChanged($event) {
    this.value = !this.value;
    this.onChange(this.value);
    // this.valueChange.emit(this.value);
  }

  // Register for change
  public registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  // Register for touch/click
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Update the model and changes
  public writeValue(value: boolean): void {
    if (this.value !== value) {
      this.value = value;
      this.onChange(this.value);
    }
  }

  public isValid(): boolean {
    return this.control && !this.control.pending && !this.control.errors && !this.control.invalid;
  }

  // validate
  public validate(control: FormControl): any {
    this.control = control;
    return control.valid ? null : control.errors;
  }
}
